/**
 * HTTP client for teleswap-bot's ZcashWallet endpoints.
 *
 * All requests carry the Telegram WebApp initData in a header — the
 * bot validates the HMAC against the bot token (see
 * teleswap-bot/src/server/init-data.ts) before serving any ZcashWallet
 * route. In a browser-preview context (no Telegram), the header is
 * empty and the bot will 401; the wallet UI falls through to a
 * "preview mode" placeholder for contacts.
 */

// Empty = use same-origin (relative paths). nginx proxies /api/telecrypt
// and /params/v1 to the bot. Set to an absolute URL only for cross-origin
// dev setups (e.g. when the app runs on :5173 but the bot is on :8080
// without a Vite proxy).
const BOT_BASE_URL =
  (import.meta.env.VITE_TELESWAP_BOT_URL as string | undefined) ?? "";

function getInitData(): string {
  const w = (typeof window !== "undefined"
    ? (window as Window & {
        Telegram?: { WebApp?: { initData?: string } };
      })
    : null);
  return w?.Telegram?.WebApp?.initData ?? "";
}

async function botFetch<T>(
  path: string,
  init?: RequestInit,
): Promise<T> {
  // Same-origin when no base configured; otherwise prepend the bot host.
  const url = BOT_BASE_URL
    ? `${BOT_BASE_URL.replace(/\/+$/, "")}${path}`
    : path;
  const res = await fetch(url, {
    ...init,
    headers: {
      ...(init?.headers ?? {}),
      "Telegram-Init-Data": getInitData(),
      "Content-Type": "application/json",
    },
  });
  const text = await res.text();
  let body: unknown = text;
  try {
    body = text ? (JSON.parse(text) as unknown) : null;
  } catch {
    // not JSON
  }
  if (!res.ok) {
    const message =
      typeof body === "object" && body !== null && "error" in body
        ? String((body as { error: unknown }).error)
        : `HTTP ${res.status}`;
    throw new TeleswapBotError(res.status, message);
  }
  return body as T;
}

export class TeleswapBotError extends Error {
  public readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = "TeleswapBotError";
    this.status = status;
  }
}

export interface TelecryptAddress {
  tg_username: string | null;
  unified_address: string;
  registered_at: number;
  updated_at: number;
}

/**
 * Register the calling user's Unified Address. Idempotent; safe to
 * call on every wallet unlock (the bot does an UPSERT keyed by
 * tg_user_id).
 */
export async function registerMyTelecryptUA(args: {
  unified_address: string;
}): Promise<TelecryptAddress> {
  const res = await botFetch<{ address: TelecryptAddress }>(
    "/api/telecrypt/register",
    {
      method: "POST",
      body: JSON.stringify({ unified_address: args.unified_address }),
    },
  );
  return res.address;
}

/** Returns my own registration row, or null if I haven't registered yet. */
export async function getMyTelecryptUA(): Promise<TelecryptAddress | null> {
  const res = await botFetch<{ address: TelecryptAddress | null }>(
    "/api/telecrypt/me",
    { method: "GET" },
  );
  return res.address;
}

/** Resolve `@alice` → UA. Throws TeleswapBotError(404) if not found. */
export async function resolveTelecryptUsername(
  username: string,
): Promise<TelecryptAddress> {
  const handle = username.startsWith("@") ? username.slice(1) : username;
  const res = await botFetch<{ address: TelecryptAddress }>(
    `/api/telecrypt/resolve/${encodeURIComponent(handle)}`,
    { method: "GET" },
  );
  return res.address;
}

export interface ChatSavedAddress {
  unified_address: string;
  label: string | null;
  saved_at: number;
  last_seen_at: number;
}

/**
 * UAs the user has DM'd to the bot. Returned newest-first.
 * Failures are silent — the Contacts screen just falls back to the
 * local recents list.
 */
export async function getChatSavedAddresses(): Promise<ChatSavedAddress[]> {
  try {
    const res = await botFetch<{ addresses: ChatSavedAddress[] }>(
      "/api/telecrypt/saved-addresses",
      { method: "GET" },
    );
    return res.addresses ?? [];
  } catch {
    return [];
  }
}

/** Remove a UA from the chat-saved book. Silent on failure. */
export async function deleteChatSavedAddress(unifiedAddress: string): Promise<void> {
  try {
    await botFetch<unknown>(
      `/api/telecrypt/saved-addresses/${encodeURIComponent(unifiedAddress)}`,
      { method: "DELETE" },
    );
  } catch {
    // best-effort; UI will re-show until next refresh
  }
}

/**
 * Update the display label on a chat-saved UA. Best-effort like
 * {@link deleteChatSavedAddress} — older bot builds may not have the
 * PUT handler yet, in which case the local optimistic UI still shows
 * the rename until the next refresh from `getChatSavedAddresses`.
 */
export async function updateChatSavedLabel(
  unifiedAddress: string,
  label: string,
): Promise<void> {
  try {
    await botFetch<unknown>(
      `/api/telecrypt/saved-addresses/${encodeURIComponent(unifiedAddress)}`,
      {
        method: "PUT",
        body: JSON.stringify({ label }),
      },
    );
  } catch {
    // best-effort; UI keeps the optimistic rename until next refresh
  }
}

export interface SwapStatusResponse {
  /** "pending" | "confirmed" | "failed" — coarse status the UI can render. */
  status: "pending" | "confirmed" | "failed";
  /** Free-form upstream stage text, e.g. "outbound broadcast". */
  stage: string | null;
  /** When the upstream stage was last polled. */
  polled_at: number;
}

/** Pull swap status by deposit tx hash. Returns null on any failure. */
export async function fetchSwapStatus(
  txHash: string,
): Promise<SwapStatusResponse | null> {
  try {
    return await botFetch<SwapStatusResponse>(
      `/api/telecrypt/swap-status/${encodeURIComponent(txHash)}`,
      { method: "GET" },
    );
  } catch {
    return null;
  }
}

// ─── Shieldz Payments (buyer-side) ──────────────────────────────────────
// These endpoints are public (no Telegram-Init-Data required) because the
// buyer needs to call them before paying. The client still sends the
// header — the bot's payments-routes ignores it on public paths but the
// existing helper is what we already have. Keeps a single fetch surface.

export interface PaymentInvoice {
  /** public_id */
  id: string;
  object: "invoice";
  amount_usd_cents: number;
  memo: string | null;
  settlement: { chain: string; asset: string; address: string };
  customer_email: string | null;
  metadata: unknown | null;
  status: "pending" | "paid" | "expired" | "failed";
  expires_at: number;
  paid_after_expiry: boolean;
  created_at: number;
  paid_at: number | null;
  mode: "live" | "test";
  pay_url: string;
  quote: PaymentInvoiceQuote | null;
}

export interface PaymentInvoiceQuote {
  amount_zec_atomic: string;
  expected_out_atomic: string;
  expected_out_usd: number;
  deposit_address: string;
  deposit_memo: string;
  fees: unknown[];
  expires_at_ms: number;
  ttl_seconds: number;
}

/** GET /api/v1/invoices/:public_id — capability token read. */
export async function fetchPaymentInvoice(
  publicId: string,
): Promise<PaymentInvoice> {
  return botFetch<PaymentInvoice>(
    `/api/v1/invoices/${encodeURIComponent(publicId)}`,
    { method: "GET" },
  );
}

/**
 * POST /api/v1/invoices/:public_id/quote — bind a fresh LeoKit quote for
 * the buyer. The buyer's refund t-address is required (the wallet derives
 * one from the user's UFVK and passes it here).
 */
export async function createInvoiceQuote(
  publicId: string,
  buyerRefundTaddr: string,
): Promise<PaymentInvoiceQuote> {
  return botFetch<PaymentInvoiceQuote>(
    `/api/v1/invoices/${encodeURIComponent(publicId)}/quote`,
    {
      method: "POST",
      body: JSON.stringify({ buyer_refund_taddr: buyerRefundTaddr }),
    },
  );
}

/**
 * POST /api/v1/invoices/:public_id/notify — after the wallet broadcasts
 * the shielded Zcash TX carrying the MAYA memo, fire-and-forget this so
 * the bot's payment observer starts polling LeoKit for the swap result.
 */
export async function notifyInvoicePayment(
  publicId: string,
  zecTxHash: string,
): Promise<{ object: "payment"; status: string; zec_tx_hash: string }> {
  return botFetch<{ object: "payment"; status: string; zec_tx_hash: string }>(
    `/api/v1/invoices/${encodeURIComponent(publicId)}/notify`,
    {
      method: "POST",
      body: JSON.stringify({ zec_tx_hash: zecTxHash }),
    },
  );
}
